import * as Sentry from "@sentry/vue";

// Works with:
// - "/api"
// - "http://localhost:3000"
// - "https://xxxx.ngrok-free.app"
// e.g. http://localhost:3000 -> ws://localhost:3000/ws/chat/{session_id}
function buildTextChatWsUrl({ apiBaseUrl, sessionId, token }) {
  const base =
    typeof apiBaseUrl === "string" && apiBaseUrl.startsWith("/")
      ? window.location.origin
      : undefined;

  const u = new URL(apiBaseUrl, base);

  u.protocol = u.protocol === "https:" ? "wss:" : "ws:";

  const basePath = u.pathname.replace(/\/+$/, "");
  u.pathname = `${basePath}/ws/chat/${sessionId}`;

  if (token) u.searchParams.set("token", token);

  return u.toString();
}

export class TextChatClient {
  constructor({ apiBaseUrl }) {
    this.apiBaseUrl = apiBaseUrl;
    this.ws = null;
    this.connected = false;
    this.sessionId = null;

    this.onEvent = null;
    this.onOpen = null;
    this.onClose = null;
    this.onError = null;

    this._sendQueue = [];
  }

  connect({ sessionId }) {
    if (!sessionId) throw new Error("sessionId is required");
    if (this.ws && (this.connected || this.ws.readyState === WebSocket.CONNECTING)) return;

    this.sessionId = sessionId;
    const token = sessionStorage.getItem("authToken");
    const url = buildTextChatWsUrl({
      apiBaseUrl: this.apiBaseUrl,
      sessionId,
      token,
    });

    this.ws = new WebSocket(url);
    this.connected = false;

    this.ws.onopen = () => {
      this.connected = true;
      Sentry.addBreadcrumb({
        category: "ws.text_chat",
        message: "WebSocket opened",
        level: "info",
        data: { session_id: this.sessionId },
      });
      this.onOpen?.();

      while (this._sendQueue.length) {
        this.ws.send(this._sendQueue.shift());
      }
    };

    this.ws.onmessage = (e) => {
      let obj;
      try {
        obj = JSON.parse(e.data);
      } catch {
        return;
      }
      this.onEvent?.(obj);
    };

    this.ws.onerror = (err) => {
      // The browser's Event for a WebSocket error carries no code/reason --
      // that detail (if any) arrives separately via onclose. This capture
      // exists to mark that an error occurred at all, since it's otherwise
      // invisible outside a console the participant never has open.
      Sentry.captureMessage("TextChat WebSocket error", {
        level: "error",
        tags: { session_id: this.sessionId },
      });
      this.onError?.(err);
    };

    this.ws.onclose = (ev) => {
      // code 1000 ("Normal Closure") is what the app itself sends via
      // close()/forceEnd flows -- not worth an issue. Anything else (1006
      // "Abnormal Closure" from a dropped connection, 1001 "Going Away" from
      // a backgrounded/killed tab, etc.) is the signal a network problem
      // actually left behind, so it's reported rather than only logged.
      if (ev && ev.code !== 1000) {
        Sentry.captureMessage("TextChat WebSocket closed abnormally", {
          level: "warning",
          tags: { session_id: this.sessionId },
          extra: { code: ev.code, reason: ev.reason, wasClean: ev.wasClean },
        });
      }
      this.connected = false;
      this.ws = null;
      this.onClose?.(ev);
    };
  }

  sendUserMessage({ content, past_session_ids = [], message_id }) {
    const payload = JSON.stringify({
      type: "user_message",
      past_session_ids,
      message_id,
      content,
    });

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      Sentry.addBreadcrumb({
        category: "ws.text_chat",
        message: "User message queued -- socket not open",
        level: "warning",
        data: { session_id: this.sessionId, ready_state: this.ws?.readyState },
      });
      this._sendQueue.push(payload);
    } else {
      this.ws.send(payload);
    }
  }

  // Demo-only: force-end the current session (backend gates on the flag).
  // The server replies with session_ended, then new_session_available or
  // summary_pending, all delivered through onEvent.
  forceEnd() {
    const payload = JSON.stringify({ type: "force_end" });

    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this._sendQueue.push(payload);
    } else {
      this.ws.send(payload);
    }
  }

  close() {
    try {
      this.ws?.close(1000, "client_close");
    } catch {
      console.error("connection close error");
    }
    this.ws = null;
    this.connected = false;
    this._sendQueue = [];
  }
}
